import type { BlockDto } from '@klickibunti/pagebuilder-react';
import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import Image from 'next/image';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { withAssetPrefix } from '@/lib/assetPrefix';
import type { EventTrackingResolved } from '@/lib/gtm/pushCmsEventTracking';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { locationMapsUrl } from '@/lib/location/mapsUrl';

export interface LocationCardConfig extends BlockDto {
  locationTitle: string;
  primaryCtaLabel: string;
  primaryCtaHref: string;
  promoText: string;
  secondaryCtaLabel: string;
  secondaryCtaHref: string;
  primaryCtaTracking?: EventTrackingResolved[];
  secondaryCtaTracking?: EventTrackingResolved[];
}

export interface LocationCardData {
  locationTitle: string;
  street: string;
  zipCode: string;
  cityName: string;
  primaryCta: {
    label: string;
    href: string;
    tracking?: EventTrackingResolved[];
  };
  promoText: string;
  secondaryCta: {
    label: string;
    href: string;
    tracking?: EventTrackingResolved[];
  };
}

interface LocationCardProps {
  data: LocationCardData;
  className?: string;
  /**
   * Heading level for the location name. `h1` on a Standort page, where this
   * name — not the Hero's headline, which is shared by every location — is
   * what the URL is about (SH-469); `h2` anywhere else, under the Hero's own
   * h1. Purely semantic: every property the base heading styles set is already
   * pinned by the utilities on the element, `tracking-tight` included, so the
   * two levels render identically.
   */
  titleAs?: 'h1' | 'h2';
}

const PinAddress = ({
  street,
  zipCode,
  cityName,
}: {
  street: string;
  zipCode: string;
  cityName: string;
}) => {
  const mapsUrl = locationMapsUrl({
    name: `Schülerhilfe Nachhilfe ${cityName}`,
    street,
    zipCode,
    cityName,
  });

  return (
    <a
      href={mapsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary text-center text-base hover:underline md:text-lg"
    >
      <Icon
        src="/icons/map-pin.svg"
        size={20}
        className="mr-1.5 mb-0.5 inline-block align-text-bottom"
      />
      {street}, {zipCode} {cityName}
    </a>
  );
};

export const LocationCard = ({
  data,
  className = '',
  titleAs: TitleTag = 'h2',
}: LocationCardProps) => {
  const [promoHeadline, promoSubtext] = data.promoText.split('\n');
  const primaryCta = resolvePrimaryCta(data.primaryCta.href);

  return (
    <div
      className={classNames(
        'section-fade-bottom md:section-fade text-foreground flex w-full flex-col rounded-t-2xl bg-white p-6 pb-0 text-center md:pb-6',
        className,
      )}
    >
      <TitleTag className="text-lg leading-tight font-medium tracking-tight lg:text-xl lg:font-bold">
        {data.locationTitle}
      </TitleTag>

      <div className="order-8 my-2 font-medium md:order-1 md:my-4 lg:order-2">
        <PinAddress
          street={data.street}
          zipCode={data.zipCode}
          cityName={data.cityName}
        />
      </div>

      {data.primaryCta.label && primaryCta && (
        <Button
          href={primaryCta.href}
          variant="primary"
          size="md"
          onClick={() =>
            pushCmsEventTracking(data.primaryCta.tracking, {
              label: data.primaryCta.label,
              ...(primaryCta.phone ? { phone: primaryCta.phone } : {}),
            })
          }
          className="order-4 mt-0 w-full py-4! md:py-3! lg:order-3"
        >
          {primaryCta.phone && (
            // Named, not decorative — this is the icon SH-533 reported. It
            // used to be `alt=""`, which put the only unlabelled `<img>` on
            // a Standort page and reads to an SEO crawl as a picture nobody
            // described.
            //
            // The name earns its place rather than just silencing the crawl:
            // the button's own label is the bare number and opening hours
            // ("030/84853790 - Heute 7-22 Uhr"), so nothing in it says what
            // the number is *for*. With the alt, the link announces as
            // "Telefon 030/84853790 - Heute 7-22 Uhr".
            //
            // German literal, like the six sibling phone affordances that
            // write `Anrufen: ...` / `Telefon ...` the same way (Header,
            // Hero Cards, Subjects, Big Offer Card, CtaActionButton). This
            // component takes every other string from `data` and has no
            // `useTranslations`; adding one here for a single word would
            // need an intl provider around it and its tests.
            <Image
              src={withAssetPrefix('/icons/phone.svg')}
              alt="Telefon"
              width={16}
              height={16}
              className="shrink-0"
            />
          )}
          <span>{data.primaryCta.label}</span>
        </Button>
      )}

      <div className="order-3 my-4 leading-0 md:order-4 lg:mt-8">
        <div className="text-base font-medium">{promoHeadline}</div>
        {promoSubtext && (
          <div className="text-sm lg:text-base">{promoSubtext}</div>
        )}
      </div>

      {/* An empty href is an unset or unresolved target (70.11 § Link Targets). */}
      {data.secondaryCta.label && data.secondaryCta.href && (
        <Button
          href={data.secondaryCta.href}
          size="md"
          variant="secondary"
          onClick={() => {
            pushCmsEventTracking(data.secondaryCta.tracking, {
              label: data.secondaryCta.label,
            });
          }}
          className="order-5 mt-4 w-full py-4! md:py-3! lg:order-6"
        >
          <Icon src="/icons/calendar.svg" size={16} />
          {data.secondaryCta.label}
        </Button>
      )}

      <hr className="border-card-border order-6 m-10 border-t lg:hidden" />
      <p className="order-7 m-0 font-bold md:hidden">Hier finden Sie uns:</p>
    </div>
  );
};

/**
 * The primary CTA is a call button by design — phone glyph, `tel:` href — but
 * the field is free text and editors also put anchors and full URLs in it.
 * Deciding per value keeps the call case intact while stopping the two ways
 * this produced a dead link:
 *
 *  - a non-phone href (`#beratungstermin`) had every character stripped by the
 *    `tel:` normalisation and rendered as `href="tel:"`, dialling nothing;
 *  - an unresolved `{location.phone}` did the same, and being a non-empty
 *    string it also defeated the `|| fallbackPhone` chain in the Hero.
 *
 * Returns `null` when nothing usable is left, in which case the CTA is not
 * rendered at all — the same rule the Hero Card's phone action follows.
 */
function resolvePrimaryCta(
  raw: string,
): { href: string; phone?: string } | null {
  const value = raw.trim();
  // A literal token means the placeholder found nothing. In body text that is
  // a deliberate visible-gap signal; in an `href` it is only a broken link.
  if (!value || /\{[^}]+\}/.test(value)) {
    return null;
  }
  const isTel = value.startsWith('tel:');
  const digits = (isTel ? value.slice('tel:'.length) : value).replace(
    /[^+\d]/g,
    '',
  );
  if (isTel || /^[+\d][\d\s/()-]*$/.test(value)) {
    return digits ? { href: `tel:${digits}`, phone: digits } : null;
  }
  return { href: value };
}
